import React, { FunctionComponent, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  SxProps,
  TextFieldProps,
} from '@mui/material';

import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../../constants';
import { TPTextField } from '../../../../components/Inputs';
import { deleteUser } from '../../../../store/UserSlice/UserApi';

const actionButtonStyles: SxProps = {
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
  width: '120px',
  height: '40px',
  boxShadow: 'none',
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  fullWidth: true,
  InputLabelProps: {
    shrink: true,
  },
};

type TState = {
  password: string;
  isPasswordVisible: boolean;
};

const initialState: TState = {
  password: '',
  isPasswordVisible: false,
};

interface AccountDeleteModalProps {
  open: boolean;
  onClose: () => void;
}

const AccountDeleteModal: FunctionComponent<AccountDeleteModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled = !state.password.trim().length;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      isPasswordVisible: !prevState.isPasswordVisible,
    }));
  };

  const handleDelete = async () => {
    setLoading(true);
    await dispatch(
      deleteUser({
        password: state.password,
      })
    );
    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { padding: '20px', borderRadius: '20px' } }}
    >
      <Stack alignItems="flex-end">
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: '#000000' }} />
        </IconButton>
      </Stack>
      <DialogTitle
        sx={{
          color: TP_COLORS.gray.darken,
          textAlign: 'center',
          fontWeight: '700',
          fontSize: '28px',
        }}
      >
        Are you sure ?
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <DialogContentText
          textAlign="center"
          color={TP_COLORS.gray.darken}
          maxWidth="514px"
        >
          Deleting your account will remove all of your information from our
          database. This action cannot be undone
        </DialogContentText>
        <FormControl sx={{ width: '350px', margin: '42px 0px' }}>
          <TPTextField
            {...textFieldProps}
            type={state.isPasswordVisible ? 'text' : 'password'}
            name="password"
            label="Password"
            value={state.password}
            onChange={handleInputChange}
            placeholder="Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility}>
                    {state.isPasswordVisible ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={onClose}
            sx={{
              ...actionButtonStyles,
              backgroundColor: TP_COLORS.border.color1,
            }}
          >
            Dismiss
          </Button>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            onClick={handleDelete}
            color="error"
            disabled={isSubmitDisabled}
            sx={{
              ...actionButtonStyles,
              color: TP_COLORS.white,
              backgroundColor: TP_COLORS.orange.light,
            }}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default AccountDeleteModal;
